import { LocalDate } from "@js-joda/core";
import { CustomIcon } from "components/icon/customIcon.component";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { parseIsoDateToLocalDateOrNull } from "web-apps/company/utils/timeAndDate";
import type { EmploymentDetailsForm } from "../../profilePanel.contract";
import { canApplyMonthlyEffectiveDate } from "../profilePanel.effectiveDate.logic";
import {
  canDeleteHistoryItemForEmploymentStartDate,
  sortHistoryItemsByStartDateDesc,
} from "../profilePanel.history.logic";
import {
  SalaryModalAddAction,
  SalaryModalDeleteButton,
  SalaryModalError,
  SalaryModalFormGrid,
  SalaryModalHistoryCell,
  SalaryModalHistoryHeaderCell,
  SalaryModalHistoryHeaderRow,
  SalaryModalHistoryRow,
  SalaryModalHistorySection,
  SalaryModalHistoryTable,
  SalaryModalHistoryTitle,
} from "./profilePanel.salaryModal.styled";

interface SalaryHistoryItem {
  id: number;
  startDate: string;
  value: number | null;
}

const salaryFormatter = new Intl.NumberFormat("sv-SE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface ProfilePanelSalaryModalProps {
  isOpen: boolean;
  isSaving: boolean;
  saveError: string;
  editedEmploymentDetails: EmploymentDetailsForm;
  salaryHistory: SalaryHistoryItem[];
  salaryType: string | null | undefined;
  effectiveFromDate: LocalDate | null;
  earliestSelectableDate: LocalDate | null;
  onClose: () => void;
  onConfirm: () => void;
  onAddSalary: () => void;
  onDeleteSalary: (salaryId: number) => void;
  onChangeFromDate: (date: LocalDate | null) => void;
  onEmploymentFieldChange: <TField extends keyof EmploymentDetailsForm>(
    field: TField,
    value: EmploymentDetailsForm[TField],
  ) => void;
}

const toHistorySalary = (value: number | null): string => {
  if (value == null || !Number.isFinite(value)) return "-";
  return salaryFormatter.format(value);
};

/**
 * Handles profile panel salary modal.
 */
export const ProfilePanelSalaryModal = (
  props: ProfilePanelSalaryModalProps,
): JSX.Element => {
  const { t } = useTranslation();

  const sortedHistory = useMemo(() => {
    return sortHistoryItemsByStartDateDesc(props.salaryHistory);
  }, [props.salaryHistory]);

  const canAddSalary = canApplyMonthlyEffectiveDate(
    props.effectiveFromDate,
    props.earliestSelectableDate,
  );

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      label={t("Staff.ProfilePanelChangeSalary")}
      size="compact"
      footer={
        <>
          <SecondaryButton
            label={t("General.Cancel")}
            disabled={props.isSaving}
            onClick={props.onClose}
          />
          <PrimaryButton
            label={t("General.Save")}
            isLoading={props.isSaving}
            disabled={props.isSaving}
            onClick={props.onConfirm}
          />
        </>
      }
    >
      <SalaryModalFormGrid>
        <InputField
          fullWidth
          label={t("Staff.ProfilePanelSalaryNewSalary")}
          value={props.editedEmploymentDetails.salary}
          onChange={(salary) => props.onEmploymentFieldChange("salary", salary)}
        />
        <DatePicker
          label={t("Staff.ProfilePanelSalaryStartDay")}
          value={props.effectiveFromDate}
          onChange={props.onChangeFromDate}
          monthlySelectableDay={1}
          earliestSelectableDate={props.earliestSelectableDate ?? undefined}
        />
        <SalaryModalAddAction>
          <PrimaryButton
            size="small"
            label={t("General.Add")}
            isLoading={props.isSaving}
            disabled={!canAddSalary || props.isSaving}
            onClick={props.onAddSalary}
          />
        </SalaryModalAddAction>
      </SalaryModalFormGrid>

      <SalaryModalHistorySection>
        <SalaryModalHistoryTitle>
          {t("General.History")}
        </SalaryModalHistoryTitle>
        <SalaryModalHistoryTable>
          <SalaryModalHistoryHeaderRow $columns={4}>
            <SalaryModalHistoryHeaderCell>
              {t("Staff.AddStaffPanelFieldStartDate")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell $isRightAligned>
              {t("Staff.AddStaffPanelFieldSalaryAmount")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell>
              {t("Staff.AddStaffPanelFieldSalaryModel")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell $isCentered>
              {""}
            </SalaryModalHistoryHeaderCell>
          </SalaryModalHistoryHeaderRow>
          {sortedHistory.length === 0 ? (
            <SalaryModalHistoryRow $columns={4}>
              <SalaryModalHistoryCell>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell $isRightAligned>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell $isCentered>-</SalaryModalHistoryCell>
            </SalaryModalHistoryRow>
          ) : (
            sortedHistory.map((salaryItem) => (
              <SalaryModalHistoryRow key={salaryItem.id} $columns={4}>
                <SalaryModalHistoryCell>
                  {parseIsoDateToLocalDateOrNull(salaryItem.startDate)?.toString() ??
                    "-"}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell $isRightAligned>
                  {toHistorySalary(salaryItem.value)}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell>
                  {props.salaryType ? t(`Staff.salaryType${props.salaryType}`) : "-"}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell $isCentered>
                  {canDeleteHistoryItemForEmploymentStartDate(
                    salaryItem.startDate,
                    props.earliestSelectableDate?.toString() ?? null,
                  ) && (
                    <SalaryModalDeleteButton
                      type="button"
                      aria-label={t("General.Remove")}
                      disabled={props.isSaving}
                      onClick={() => props.onDeleteSalary(salaryItem.id)}
                    >
                      <CustomIcon name="v2/trash" size="16px" />
                    </SalaryModalDeleteButton>
                  )}
                </SalaryModalHistoryCell>
              </SalaryModalHistoryRow>
            ))
          )}
        </SalaryModalHistoryTable>
      </SalaryModalHistorySection>

      {props.saveError.length > 0 && (
        <SalaryModalError>{props.saveError}</SalaryModalError>
      )}
    </Modal>
  );
};
